import translations from "../common/Translations";

/**
 * Presentation helpers shared by the bulk PDF export progress modal and the
 * export-history screen (BLF-2171).
 */

export const BULK_EXPORT_STATUS = {
  PENDING: "Pending",
  IN_PROGRESS: "InProgress",
  COMPLETED: "Completed",
  FAILED: "Failed",
};

/** Pending/InProgress exports are still moving, so the UI keeps refreshing them. */
export const isBulkExportActive = (status) =>
  status === BULK_EXPORT_STATUS.PENDING || status === BULK_EXPORT_STATUS.IN_PROGRESS;

export const getBulkExportStatusMeta = (status, langMode) => {
  const t = translations[langMode] ?? {};

  switch (status) {
    case BULK_EXPORT_STATUS.COMPLETED:
      return {
        label: t.bulkExportStatusCompleted ?? "Completed",
        badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
        barClass: "bg-emerald-500",
      };
    case BULK_EXPORT_STATUS.FAILED:
      return {
        label: t.bulkExportStatusFailed ?? "Failed",
        badgeClass: "bg-red-50 text-red-700 border-red-200",
        barClass: "bg-red-500",
      };
    case BULK_EXPORT_STATUS.IN_PROGRESS:
      return {
        label: t.bulkExportStatusInProgress ?? "In progress",
        badgeClass: "bg-blue-50 text-[#236DB4] border-blue-200",
        barClass: "bg-[#236DB4]",
      };
    case BULK_EXPORT_STATUS.PENDING:
    default:
      return {
        label: t.bulkExportStatusPending ?? "Queued",
        badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
        barClass: "bg-amber-400",
      };
  }
};

/** Clamped so a backend percentage outside 0-100 can't overflow the bar. */
export const clampPercentage = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.min(100, Math.max(0, Math.round(numeric)));
};

export const formatFileSize = (bytes) => {
  const numeric = Number(bytes);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let size = numeric;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size >= 10 || unitIndex === 0 ? Math.round(size) : size.toFixed(1)} ${units[unitIndex]}`;
};

/** Backend dates arrive as `yyyy-MM-dd` or `yyyy-MM-ddTHH:mm:ss`; only the day matters here. */
export const formatBulkExportDate = (value) => {
  if (!value) return "";
  const datePart = String(value).split("T")[0];
  const parsed = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return datePart;
  return parsed.toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" });
};

export const formatBulkExportDateRange = (fromDate, toDate) => {
  const from = formatBulkExportDate(fromDate);
  const to = formatBulkExportDate(toDate);
  if (from && to) return `${from} – ${to}`;
  return from || to || "-";
};

export const formatBulkExportTimestamp = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return `${parsed.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })} ${parsed.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
};

/**
 * `fileUrl` is a public S3 link served with `Content-Disposition: attachment`,
 * so opening it is enough -- no auth header, no blob, no memory pressure.
 */
export const openBulkExportFile = (fileUrl) => {
  if (!fileUrl) return false;
  window.open(fileUrl, "_blank", "noopener,noreferrer");
  return true;
};

/** Fills `{key}` placeholders in a translation string. */
export const fillTemplate = (template, values = {}) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.split(`{${key}}`).join(String(value)),
    String(template ?? "")
  );
